<template>
  <el-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)"
    title="Aviso de Privacidad" width="90%" :max-width="1200" :close-on-click-modal="true" :close-on-press-escape="true"
    class="privacy-dialog" append-to-body :z-index="9999" top="5vh">
    <div class="privacy-pdf-container">
      <div v-if="showPrivacyPdfFallback" class="privacy-pdf-error">
        <el-icon v-if="privacyPdfError" class="error-icon">
          <Warning />
        </el-icon>
        <p class="error-message">
          <span v-if="isEmbedded">Para ver el Aviso de Privacidad, ábrelo o descárgalo con los enlaces de abajo.</span>
          <span v-else>No se pudo cargar el Aviso de Privacidad. Por favor, intenta abrirlo en una nueva pestaña.</span>
        </p>
        <a v-if="privacyPdfError" :href="privacyPdfUrl" target="_blank" rel="noopener noreferrer"
          class="privacy-pdf-link privacy-pdf-link-fallback">
          <el-icon>
            <View />
          </el-icon>
          <span>Abrir PDF en nueva pestaña</span>
        </a>
      </div>
      <iframe v-if="!showPrivacyPdfFallback" :src="privacyPdfUrl" type="application/pdf" class="privacy-pdf-iframe"
        title="Aviso de Privacidad" @error="handlePdfError" @load="handlePdfLoad" />
      <div class="privacy-pdf-footer">
        <a :href="privacyPdfUrl" target="_blank" rel="noopener noreferrer" class="privacy-pdf-link">
          <el-icon>
            <View />
          </el-icon>
          <span>Abrir en nueva pestaña</span>
        </a>
      </div>
    </div>
    <template #footer>
      <el-button type="primary" @click="$emit('update:modelValue', false)">Cerrar</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { View, Warning } from '@element-plus/icons-vue';
import { getPrivacyPdfUrl } from '@/config/api';

defineProps<{
  modelValue: boolean;
}>();

defineEmits<{
  'update:modelValue': [value: boolean];
}>();

// Estado para manejar errores de carga del PDF
const privacyPdfError = ref(false);

// URL del PDF del aviso de privacidad
const privacyPdfUrl = getPrivacyPdfUrl();

// Cuando el widget está embebido en otro sitio, el iframe del PDF puede ser bloqueado por "refused to connect"
// (X-Frame-Options / CSP del servidor). Mostramos solo los enlaces para abrir/descargar en lugar del iframe.
const isEmbedded = computed(() => typeof window !== 'undefined' && window !== window.top);
const showPrivacyPdfFallback = computed(() => privacyPdfError.value || isEmbedded.value);

// Verificar que el PDF se cargue correctamente
const handlePdfLoad = async (event: Event): Promise<void> => {
  const iframe = event.target as HTMLIFrameElement;

  try {
    // Intentar acceder al contenido del iframe para verificar que sea un PDF
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

    // Si el iframe contiene HTML (como index.html), significa que el PDF no existe
    if (iframeDoc && iframeDoc.documentElement) {
      const isHtml = iframeDoc.documentElement.tagName === 'HTML';
      const hasBody = iframeDoc.body !== null;

      if (isHtml && hasBody) {
        // Verificar si el contenido es HTML (no PDF)
        const bodyText = iframeDoc.body.textContent || '';
        const isAppContent = bodyText.includes('Comparador') ||
          bodyText.includes('Titan') ||
          bodyText.includes('404') ||
          iframeDoc.querySelector('script') !== null;

        if (isAppContent) {
          privacyPdfError.value = true;
        }
      }
    }
  } catch (error) {
    // Si hay error de CORS al acceder al iframe, asumimos que es un PDF válido
    // (los PDFs no permiten acceso cross-origin, pero HTML sí)
  }
};

const handlePdfError = (): void => {
  privacyPdfError.value = true;
};
</script>

<!--
  Estilos GLOBALES para el diálogo teleportado con append-to-body.
  No pueden ser scoped porque el DOM del diálogo se teleporta al <body>,
  fuera del scope del componente Vue.
-->
<style>
/* ===== Estilos del diálogo de Aviso de Privacidad (global, para append-to-body) ===== */
/* Usar selectores robustos que funcionen aunque el diálogo sea movido a un portal
   y la clase `privacy-dialog` viva directamente en el nodo `.el-dialog`. */
.el-dialog.privacy-dialog {
  margin-top: 5vh !important;
  height: min(85vh, 900px) !important;
  max-height: 95vh !important;
  display: flex !important;
  flex-direction: column !important;
}

.el-dialog.privacy-dialog .el-dialog__header {
  flex-shrink: 0;
}

.el-dialog.privacy-dialog .el-dialog__body {
  padding: 0 !important;
  flex: 1 !important;
  min-height: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.el-dialog.privacy-dialog .el-dialog__footer {
  flex-shrink: 0;
}

.privacy-pdf-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f3f4f6;
  position: relative;
  overflow: hidden;
}

.privacy-pdf-iframe {
  width: 100%;
  flex: 1;
  border: none;
  background: #ffffff;
}

.privacy-pdf-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  gap: 16px;
  background: #f9fafb;
}

.error-icon {
  font-size: 48px;
  color: #fbbf24;
}

.error-message {
  color: #4b5563;
  margin: 0;
  font-size: 16px;
  max-width: 400px;
  line-height: 1.5;
}

.privacy-pdf-footer {
  padding: 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-shrink: 0;
}

.widget-compact-mode .privacy-pdf-footer {
  padding: 12px !important;
  gap: 12px !important;
}

.privacy-pdf-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #3b82f6;
  color: #ffffff;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
}

.privacy-pdf-link-secondary {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.privacy-pdf-link-fallback {
  margin-top: 16px;
}

.privacy-pdf-link:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.privacy-pdf-link-secondary:hover {
  background: #f3f4f6;
  color: #111827;
  border-color: #9ca3af;
}

.privacy-pdf-link .el-icon {
  font-size: 18px;
}

@media (max-width: 640px) {
  .el-dialog.privacy-dialog {
    width: 95% !important;
    margin-top: 2vh !important;
    max-height: 96vh !important;
  }

  .el-dialog.privacy-dialog .el-dialog__body {
    height: 80vh !important;
  }

  .privacy-pdf-footer {
    flex-direction: column;
  }

  .privacy-pdf-link {
    justify-content: center;
    width: 100%;
  }
}
</style>
